import React, { useState, useEffect } from "react"
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Input,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import Select from "react-select"
import { get } from "../../helpers/api_helper"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { Trans } from "react-i18next"
import { ITEMS_PER_PAGE } from "helpers/pagination_helper"
import Loader from "common/loader"
import { SMS_LOGS_LIST, SHOW_SMS_LOG } from "helpers/api_endpoints"
import moment from "moment"
//meta title
document.title = "SMS Logs"

const statuses = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "queued", label: "Queued" },
  { value: "sent", label: "Sent" },
  { value: "delivered", label: "Delivered" },
  { value: "undelivered", label: "Undelivered" },
  { value: "failed", label: "Failed" },
]

const purposes = [
  { value: "", label: "All" },
  { value: "otp", label: "OTP" },
  { value: "delivery_otp", label: "Delivery OTP" },
  { value: "payment_link", label: "Payment Link" },
  { value: "emergency", label: "Emergency" },
  { value: "other", label: "Other" },
]

const statusColors = {
  delivered: "bg-success",
  sent: "bg-info",
  queued: "bg-warning",
  pending: "bg-warning",
  undelivered: "bg-danger",
  failed: "bg-danger",
}

const purposeLabel = value =>
  purposes.find(p => p.value === value)?.label || value

// numbers are stored as digits only, without "+" or a leading "00"
const normalizeNumber = value => value.replace(/\D/g, "").replace(/^00/, "")

const formatDate = value =>
  value ? moment(value).format("YYYY-MM-DD HH:mm") : "-"

const StatusBadge = ({ status }) => (
  <Badge className={statusColors[status] || "bg-secondary"}>
    <Trans>{statuses.find(s => s.value === status)?.label || status}</Trans>
  </Badge>
)

function SmsLogs() {
  const [loading, setLoading] = useState(true)
  const [logs, setLogs] = useState([])
  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [number, setNumber] = useState("")
  const [orderId, setOrderId] = useState("")
  const [selectedLog, setSelectedLog] = useState(null)
  const [detailsLoading, setDetailsLoading] = useState(false)

  useEffect(() => {
    const getLogs = async () => {
      try {
        setLoading(true)
        const response = await get(SMS_LOGS_LIST, {
          params: { paginate: ITEMS_PER_PAGE, page, ...filters },
        })
        const data = response.data
        setLogs(data.data)
        setTotalPages(data.pagination.totalPages)
      } catch (error) {
        console.error("Error fetching sms logs:", error)
        setLogs([])
        setTotalPages(0)
      } finally {
        setLoading(false)
      }
    }

    getLogs()
  }, [page, filters])

  // debounce the text filters, they are exact matches on the backend
  useEffect(() => {
    const timer = setTimeout(
      () => handleFilter("number", normalizeNumber(number)),
      500
    )
    return () => clearTimeout(timer)
  }, [number])

  useEffect(() => {
    const timer = setTimeout(() => handleFilter("orderId", orderId.trim()), 500)
    return () => clearTimeout(timer)
  }, [orderId])

  const handleFilter = (type, value) => {
    setFilters(prev => {
      const filtvalues = { ...prev }
      if (value === "") {
        delete filtvalues[type]
      } else {
        switch (type) {
          case "from":
            filtvalues.from = moment(value).startOf("day").toISOString()
            break
          case "to":
            filtvalues.to = moment(value).endOf("day").toISOString()
            break
          default:
            filtvalues[type] = value
        }
      }
      return filtvalues[type] === prev[type] ? prev : filtvalues
    })
    setPage(1)
  }

  const handleNextPage = () => {
    setPage(page + 1)
  }

  const handlePrevPage = () => {
    setPage(page - 1)
  }

  const openDetails = async id => {
    try {
      setDetailsLoading(true)
      setSelectedLog({ id })
      const response = await get(`${SHOW_SMS_LOG}/${id}`)
      setSelectedLog(response.data)
    } catch (error) {
      console.error("Error fetching sms log:", error)
      setSelectedLog(null)
    } finally {
      setDetailsLoading(false)
    }
  }

  const closeDetails = () => setSelectedLog(null)

  const detailRow = (label, value) => (
    <tr>
      <th style={{ width: "30%" }}>
        <Trans>{label}</Trans>
      </th>
      <td style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {value === null || value === undefined || value === "" ? "-" : value}
      </td>
    </tr>
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="SMS Logs" />

          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <div className="row">
                    <div className="form-group col-md-3 mt-2">
                      <Label className="form-label">
                        <Trans>Phone Number</Trans>
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        placeholder="971501234567"
                        value={number}
                        onChange={e => setNumber(e.target.value)}
                      />
                    </div>
                    <div className="form-group col-md-3 mt-2">
                      <Label className="form-label">
                        <Trans>Order ID</Trans>
                      </Label>
                      <Input
                        type="number"
                        className="form-control"
                        value={orderId}
                        onChange={e => setOrderId(e.target.value)}
                      />
                    </div>
                    <div className="form-group col-md-3 mt-2">
                      <Label className="form-label">
                        <Trans>Status</Trans>
                      </Label>
                      <Select
                        options={statuses}
                        isClearable
                        onChange={o => handleFilter("status", o ? o.value : "")}
                      />
                    </div>
                    <div className="form-group col-md-3 mt-2">
                      <Label className="form-label">
                        <Trans>Purpose</Trans>
                      </Label>
                      <Select
                        options={purposes}
                        isClearable
                        onChange={o =>
                          handleFilter("purpose", o ? o.value : "")
                        }
                      />
                    </div>
                    <div className="form-group col-md-3 mt-2">
                      <Label className="form-label">
                        <Trans>From Date</Trans>
                      </Label>
                      <Input
                        type="date"
                        className="form-control"
                        onChange={e => handleFilter("from", e.target.value)}
                      />
                    </div>
                    <div className="form-group col-md-3 mt-2">
                      <Label className="form-label">
                        <Trans>To Date</Trans>
                      </Label>
                      <Input
                        type="date"
                        className="form-control"
                        onChange={e => handleFilter("to", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="table-responsive mt-4">
                    <Table className="table table-striped table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>
                            <Trans>ID</Trans>
                          </th>
                          <th>
                            <Trans>Date</Trans>
                          </th>
                          <th>
                            <Trans>Phone Number</Trans>
                          </th>
                          <th>
                            <Trans>Purpose</Trans>
                          </th>
                          <th>
                            <Trans>Order ID</Trans>
                          </th>
                          <th>
                            <Trans>Status</Trans>
                          </th>
                          <th>
                            <Trans>Provider Status</Trans>
                          </th>
                          <th>
                            <Trans>Message</Trans>
                          </th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <th colSpan={9} className="text-center">
                              <Loader />
                            </th>
                          </tr>
                        ) : logs.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-center">
                              <Trans>No SMS logs found</Trans>
                            </td>
                          </tr>
                        ) : (
                          logs.map(log => (
                            <tr key={log.id}>
                              <th scope="row">{log.id}</th>
                              <td className="text-nowrap">
                                {formatDate(log.createdAt)}
                              </td>
                              <td>{log.number}</td>
                              <td>
                                <Trans>{purposeLabel(log.purpose)}</Trans>
                              </td>
                              <td>{log.orderId || "-"}</td>
                              <td>
                                <StatusBadge status={log.status} />
                              </td>
                              <td>{log.providerStatus || "-"}</td>
                              <td>
                                <div
                                  className="text-truncate"
                                  style={{ maxWidth: 250 }}
                                  title={log.text}
                                >
                                  {log.text}
                                </div>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => openDetails(log.id)}
                                >
                                  <Trans>View</Trans>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <br />
                  <div className="text-center">
                    <button
                      className="btn btn-sm btn-dark"
                      onClick={handlePrevPage}
                      disabled={page === 1}
                    >
                      <Trans>Previous</Trans>
                    </button>
                    &nbsp;
                    <button
                      className="btn btn-sm btn-dark"
                      onClick={handleNextPage}
                      disabled={page >= totalPages}
                    >
                      <Trans>Next</Trans>
                    </button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={!!selectedLog} toggle={closeDetails} size="lg">
        <ModalHeader toggle={closeDetails}>
          <Trans>SMS Details</Trans> #{selectedLog?.id}
        </ModalHeader>
        <ModalBody>
          {detailsLoading ? (
            <div className="text-center">
              <Loader />
            </div>
          ) : (
            selectedLog && (
              <div className="table-responsive">
                <Table className="table table-bordered mb-0">
                  <tbody>
                    {detailRow("Phone Number", selectedLog.number)}
                    {detailRow(
                      "Status",
                      <StatusBadge status={selectedLog.status} />
                    )}
                    {detailRow(
                      "Purpose",
                      <Trans>{purposeLabel(selectedLog.purpose)}</Trans>
                    )}
                    {detailRow("Message", selectedLog.text)}
                    {detailRow("Order ID", selectedLog.orderId)}
                    {detailRow("User ID", selectedLog.userId)}
                    {detailRow("Sender ID", selectedLog.senderId)}
                    {detailRow("Sent At", formatDate(selectedLog.createdAt))}
                    {detailRow("Status At", formatDate(selectedLog.statusAt))}
                    {detailRow("Provider Status", selectedLog.providerStatus)}
                    {detailRow("Provider Message", selectedLog.providerMessage)}
                    {detailRow("Error", selectedLog.error)}
                    {detailRow("Message UUID", selectedLog.messageUUID)}
                    {detailRow("API ID", selectedLog.apiId)}
                    {detailRow(
                      "Callback Payload",
                      selectedLog.callbackPayload ? (
                        <pre className="mb-0">
                          {JSON.stringify(selectedLog.callbackPayload, null, 2)}
                        </pre>
                      ) : null
                    )}
                  </tbody>
                </Table>
              </div>
            )
          )}
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-secondary" onClick={closeDetails}>
            <Trans>Close</Trans>
          </button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default SmsLogs
